import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axiosConfig';
import { sanitizeRedirectUrl } from '../utils/security';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const checkAuthCalled = useRef(false);
    const location = useLocation();

    // Check authentication status on mount
    useEffect(() => {
        if (checkAuthCalled.current) {
            return;
        }


        checkAuthCalled.current = true;
        checkAuth(); // Initial check
    }, []);

    const checkAuth = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/auth/verify');

            if (response.data.success && response.data.authenticated) {
                setAuthenticated(true);
                setUser(response.data.user);
            } else {
                throw new Error('Verification failed');
            }
        } catch (error) {
            setAuthenticated(false);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password, acctNo = null) => {
        try {
            const response = await api.post('/api/auth/login', {
                email,
                password,
                acctNo
            });

            if (response.data.success) {
                setAuthenticated(true);
                setUser(response.data.user);
                return { success: true, user: response.data.user };
            } else {
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    };

    const logout = async () => {
        try {
            await api.post('/api/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clear local state
            setAuthenticated(false);
            setUser(null);
        }
    };

    const googleLogin = (redirectUrl) => {
        // 🔒 SECURITY: Sanitize redirect URL to prevent open redirect attacks
        const safeRedirect = sanitizeRedirectUrl(redirectUrl, `${window.location.origin}/dashboard`);
        window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/google?redirect=${encodeURIComponent(safeRedirect)}`;
    };

    const refreshUser = async () => {
        try {
            const response = await api.get('/api/auth/me');
            if (response.data.success) {
                setUser(response.data.user);
            }
        } catch (error) {
            console.error('Failed to refresh user data:', error);
        }
    };

    const value = {
        user,
        authenticated,
        loading,
        login,
        logout,
        googleLogin,
        checkAuth,
        refreshUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
